import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogHeader, DialogBody, Button, Textarea } from '@repo/ui';
import {
  AppealStatus,
  AppealType,
  rollbackTargetsFor,
  ROLLBACK_AAS_BACKLOG_MESSAGE,
  ROLLBACK_AAS_NO_TIMEKEEPERS_MESSAGE,
  ROLLBACK_PRE_REAPPEAL_MESSAGE,
  ROLLBACK_PRE_REAPPEAL_SHORT,
  ROLLBACK_TO_AWAITING_INELIGIBLE_MESSAGE,
} from '@repo/types';
import { useGetAppealList } from '../../queries/appealQuery';
import { useGetInvoice } from '../../queries/invoiceQuery';

const MAX_COMMENT_LENGTH = 1000;

const TARGET_LABEL: Record<string, string> = {
  [AppealStatus.PRE_APPEAL]: 'Pre-Appeal',
  [AppealStatus.AWAITING_APPEAL_SUBMISSION]: 'Awaiting Appeal Submission',
};

const TARGET_SUBMIT_LABEL: Record<string, string> = {
  [AppealStatus.PRE_APPEAL]: 'Roll back to Pre-Appeal',
  [AppealStatus.AWAITING_APPEAL_SUBMISSION]: 'Roll back to Awaiting Submission',
};

export interface RollbackInvoiceModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  invoiceNumber?: string;
  invoiceId?: string;
  onClose: () => void;
  onSubmit: (targetStatus: string, comment: string) => void;
}

export const RollbackInvoiceModal: React.FC<RollbackInvoiceModalProps> = ({
  isOpen,
  isLoading = false,
  invoiceNumber,
  invoiceId,
  onClose,
  onSubmit,
}) => {
  const { data: appeals, isLoading: isLoadingAppeals } = useGetAppealList(invoiceId || '', { enabled: isOpen && !!invoiceId });
  const { data: invoice, isLoading: isLoadingInvoice } = useGetInvoice(invoiceId || '', { enabled: isOpen && !!invoiceId });

  const activeAppeal = useMemo(() => {
    if (!appeals?.length) return null;
    return appeals.find(a => a.appealStatus !== AppealStatus.CLOSED) ?? null;
  }, [appeals]);

  const canRollback = !isLoadingAppeals && !!activeAppeal;
  const isBacklog = activeAppeal?.appealType === AppealType.BACKLOG;
  const hasTimekeepers = (activeAppeal?.timekeepers?.length ?? 0) > 0;
  const writeOffOriginsKnown = invoice?.writeOffOriginsKnown;

  const availableTargets = useMemo<string[]>(() => {
    if (!activeAppeal?.appealStatus) return [AppealStatus.PRE_APPEAL];
    const targets = rollbackTargetsFor(activeAppeal.appealStatus);
    return targets.length ? targets : [AppealStatus.PRE_APPEAL];
  }, [activeAppeal?.appealStatus]);

  const showAasOption = availableTargets.includes(AppealStatus.AWAITING_APPEAL_SUBMISSION);

  // Strict === true (not !== false): a loading `undefined` keeps the radio disabled (no
  // click-then-422). BACKLOG has zero timekeepers, so this also covers it; the disabled
  // tooltip still picks its reason BACKLOG-first.
  const aasEnabled = hasTimekeepers && writeOffOriginsKnown === true;

  const aasDisabledReason = useMemo(() => {
    if (aasEnabled) return null;
    if (isBacklog) {
      return ROLLBACK_AAS_BACKLOG_MESSAGE;
    }
    if (!hasTimekeepers) {
      return ROLLBACK_AAS_NO_TIMEKEEPERS_MESSAGE;
    }
    // Still loading — eligibility unknown, not ineligible. Neutral copy avoids wrongly
    // telling the clerk the data can't be restored.
    if (isLoadingInvoice || writeOffOriginsKnown === undefined) {
      return 'Checking eligibility…';
    }
    return ROLLBACK_TO_AWAITING_INELIGIBLE_MESSAGE;
  }, [aasEnabled, isBacklog, hasTimekeepers, isLoadingInvoice, writeOffOriginsKnown]);

  // Re-appeal-born appeal (escalation→reject→re-appeal) carries a prior collection. Rolling
  // back to PRE would wipe the collection history and re-present it as never-appealed.
  const isReappealBorn = (activeAppeal?.lastCollectedAmount ?? 0) > 0;

  // PRE wipes the prior collection, so disable it for re-appeal-born appeals. When AAS is an
  // available target (e.g. APPEAL_SUBMITTED) the clerk rolls back there instead. When AAS is
  // NOT a target for this status (PRE-only statuses such as TO_BE_APPEALED / AWAITING), there
  // is no alternative: PRE stays disabled and the modal shows a "can't be rolled back" state
  // below rather than offering a submit the API would reject.
  const preDisabled = isReappealBorn && (aasEnabled || !showAasOption);
  const preDisabledReason = preDisabled ? ROLLBACK_PRE_REAPPEAL_MESSAGE : null;

  // A rollback target is usable only when it is offered (in availableTargets) AND not disabled.
  const isTargetUsable = (target: string) =>
    target === AppealStatus.AWAITING_APPEAL_SUBMISSION
      ? aasEnabled
      : target === AppealStatus.PRE_APPEAL
        ? !preDisabled
        : true;
  const hasUsableTarget = availableTargets.some(isTargetUsable);

  const [targetStatus, setTargetStatus] = useState<string>(AppealStatus.PRE_APPEAL);
  const [comment, setComment] = useState<string>('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setComment('');
      setTouched(false);
    }
  }, [isOpen]);

  // Default the target on open / once eligibility resolves to the first USABLE target, so the
  // selection never lands on a disabled or unavailable option (e.g. AAS on a PRE-only status).
  useEffect(() => {
    if (!isOpen) return;
    const firstUsable = availableTargets.find(t =>
      t === AppealStatus.AWAITING_APPEAL_SUBMISSION
        ? aasEnabled
        : t === AppealStatus.PRE_APPEAL
          ? !preDisabled
          : true,
    );
    setTargetStatus(firstUsable ?? availableTargets[0] ?? AppealStatus.PRE_APPEAL);
  }, [isOpen, preDisabled, aasEnabled, availableTargets]);

  const isOverLimit = comment.length > MAX_COMMENT_LENGTH;
  const showEmptyError = touched && !comment.trim();
  const errorMessage = showEmptyError
    ? 'Add a reason before rolling back.'
    : isOverLimit
      ? `Reason must be ${MAX_COMMENT_LENGTH} characters or fewer (currently ${comment.length})`
      : undefined;

  const handleSubmit = () => {
    if (!comment.trim()) {
      setTouched(true);
      return;
    }
    if (isOverLimit) return;
    // Never submit a disabled target (covers eligibility flipping after a manual selection;
    // the BE also guards).
    const targetDisabled =
      (targetStatus === AppealStatus.AWAITING_APPEAL_SUBMISSION && !aasEnabled) ||
      (targetStatus === AppealStatus.PRE_APPEAL && preDisabled);
    if (targetDisabled) return;
    onSubmit(targetStatus, comment.trim());
  };

  const headerTitle = invoiceNumber
    ? `Rollback Invoice #${invoiceNumber}`
    : 'Rollback Invoice';

  const isAasSelected = targetStatus === AppealStatus.AWAITING_APPEAL_SUBMISSION;

  const submitLabel = TARGET_SUBMIT_LABEL[targetStatus] ?? 'Rollback';

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      size="lg"
      closeOnBackdrop={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      ariaLabel={headerTitle}
    >
      <DialogHeader className="!px-0">
        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">{headerTitle}</h3>
      </DialogHeader>
      <DialogBody className="pl-0 pr-2 space-y-5 overflow-y-auto max-h-[70vh]">
        {isLoadingAppeals ? (
          <div className="space-y-5 animate-pulse">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-lg h-16" />
            <div className="space-y-2">
              <div className="bg-gray-200 dark:bg-gray-700 rounded h-4 w-24" />
              <div className="bg-gray-200 dark:bg-gray-700 rounded-lg h-20" />
            </div>
            <div className="space-y-2">
              <div className="bg-gray-200 dark:bg-gray-700 rounded h-4 w-36" />
              <div className="bg-gray-200 dark:bg-gray-700 rounded-lg h-28" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <div className="bg-gray-200 dark:bg-gray-700 rounded h-10 w-20" />
              <div className="bg-gray-200 dark:bg-gray-700 rounded h-10 w-24" />
            </div>
          </div>
        ) : !canRollback || !hasUsableTarget ? (
          <>
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex gap-3">
              <span className="text-gray-500 dark:text-gray-400 text-lg shrink-0">ℹ</span>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {!canRollback
                  ? "There's no active appeal on this invoice, so there's nothing to roll back."
                  : "This invoice continues an earlier appeal that already collected payment, so it can't return to Pre-Appeal, and there's no earlier appeal state to roll back to."}
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={onClose} size="md">
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Target picker */}
            <div className="space-y-2">
              <label id="rollback-target-label" className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">Roll back to</label>
              <div className="space-y-2" role="radiogroup" aria-labelledby="rollback-target-label" aria-label="Roll back to">
                {availableTargets.map(target => {
                  const isAas = target === AppealStatus.AWAITING_APPEAL_SUBMISSION;
                  const isPre = target === AppealStatus.PRE_APPEAL;
                  const isDisabled = (isAas && !aasEnabled) || (isPre && preDisabled);
                  const isSelected = targetStatus === target;
                  // Re-appeal-born PRE block gets the high-attention treatment (short amber
                  // label + "Why?" expander); the full reason stays the SR description.
                  const isPreReappeal = isPre && preDisabled;
                  const disabledReason = isDisabled
                    ? isAas
                      ? aasDisabledReason ?? undefined
                      : preDisabledReason ?? undefined
                    : undefined;
                  const reasonId = disabledReason ? `rollback-target-${target}-reason` : undefined;
                  return (
                    <label
                      key={target}
                      htmlFor={`rollback-target-${target}`}
                      title={disabledReason}
                      className={`
                        flex items-start gap-3 p-4 rounded-lg border transition-all
                        ${isDisabled
                          ? 'cursor-not-allowed opacity-60 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-xtnd-dark-800'
                          : isSelected
                            ? 'cursor-pointer border-xtnd-blue bg-xtnd-light dark:bg-xtnd-blue/20'
                            : 'cursor-pointer border-gray-300 dark:border-gray-600 bg-white dark:bg-xtnd-dark-800'}
                      `}
                    >
                      <div className="flex items-start pt-0.5">
                        <input
                          type="radio"
                          id={`rollback-target-${target}`}
                          name="rollback-target"
                          value={target}
                          checked={isSelected}
                          disabled={isDisabled || isLoading}
                          aria-disabled={isDisabled || undefined}
                          aria-describedby={reasonId}
                          onChange={() => setTargetStatus(target)}
                          className="peer sr-only"
                        />
                        <span
                          className={`
                            inline-flex items-center justify-center rounded-full border
                            transition-colors h-5 w-5
                            peer-focus-visible:ring-2 peer-focus-visible:ring-xtnd-blue peer-focus-visible:ring-offset-2
                            dark:peer-focus-visible:ring-offset-xtnd-dark-800
                            ${isSelected && !isDisabled
                              ? 'border-xtnd-blue bg-xtnd-blue'
                              : 'border-gray-400 dark:border-gray-500 bg-white dark:bg-xtnd-dark-800'}
                          `}
                        >
                          {isSelected && !isDisabled && (
                            <span className="inline-block rounded-full bg-white h-2.5 w-2.5" />
                          )}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-xtnd-dark dark:text-xtnd-light text-sm">
                          {TARGET_LABEL[target] ?? target}
                        </div>
                        {isDisabled && isPreReappeal ? (
                          // Short reason here; full message lives in an sr-only node (aria-describedby)
                          // and the "Why?" expander so SR users still get the complete explanation.
                          <div className="mt-1.5 flex items-start gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                            <span aria-hidden="true" className="shrink-0 leading-4">⚠</span>
                            <div className="space-y-1">
                              <span aria-hidden="true">{ROLLBACK_PRE_REAPPEAL_SHORT}</span>
                              <span id={reasonId} className="sr-only">{ROLLBACK_PRE_REAPPEAL_MESSAGE}</span>
                              <details>
                                <summary className="cursor-pointer underline text-amber-700/80 dark:text-amber-400/80 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
                                  Why?
                                </summary>
                                <p className="mt-1 font-normal text-amber-800/90 dark:text-amber-300/90">
                                  {ROLLBACK_PRE_REAPPEAL_MESSAGE}
                                </p>
                              </details>
                            </div>
                          </div>
                        ) : isDisabled && disabledReason ? (
                          <div id={reasonId} className="mt-1 text-xs text-xtnd-dark/60 dark:text-xtnd-light/60">
                            {disabledReason}
                          </div>
                        ) : null}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Target-aware effect banner */}
            {isAasSelected ? (
              <div className="bg-xtnd-light-100 dark:bg-xtnd-blue/10 border border-xtnd-blue dark:border-xtnd-blue-800 rounded-lg p-4 flex gap-3">
                <span className="text-xtnd-blue-700 dark:text-xtnd-blue-300 text-lg shrink-0">ℹ</span>
                <p className="text-sm text-xtnd-dark-800 dark:text-xtnd-light">
                  <strong>Can't be undone.</strong> Clears the submission and any decision; your prepared cuts, discounts, and timekeepers are kept, and <strong>timekeepers aren't re-notified.</strong>
                </p>
              </div>
            ) : showAasOption ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex gap-3">
                <span className="text-red-600 dark:text-red-400 text-lg shrink-0">⚠</span>
                <p className="text-sm text-red-800 dark:text-red-200">
                  <strong>This can't be undone.</strong> Everything on the appeal — cuts, discounts, timekeepers, and write-offs — is removed and the invoice returns to Pre-Appeal, as if no appeal was ever started.
                </p>
              </div>
            ) : (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex gap-3">
                <span className="text-red-600 dark:text-red-400 text-lg shrink-0">⚠</span>
                <p className="text-sm text-red-800 dark:text-red-200">
                  {isBacklog
                    ? 'This backlog appeal can only go back to Pre-Appeal. Everything on the appeal is removed and the invoice returns to Pre-Appeal.'
                    : 'Everything on the appeal — cuts, discounts, timekeepers, and write-offs — is removed and the invoice returns to Pre-Appeal.'}
                </p>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-2">
              <Textarea
                label="Reason for Rollback"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder="Please provide a detailed reason for rolling back this invoice..."
                rows={4}
                disabled={isLoading}
                required
                error={errorMessage}
                className="w-full"
              />
              <div
                className={`text-xs text-right ${
                  isOverLimit
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-xtnd-dark/60 dark:text-xtnd-light/60'
                }`}
              >
                {comment.length} / {MAX_COMMENT_LENGTH}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={onClose} disabled={isLoading} size="md">
                Cancel
              </Button>
              <Button
                variant={isAasSelected ? 'primary' : 'destructive'}
                onClick={handleSubmit}
                loading={isLoading}
                disabled={!comment.trim() || isOverLimit || isLoading}
                size="md"
              >
                {submitLabel}
              </Button>
            </div>
          </>
        )}
      </DialogBody>
    </Dialog>
  );
};

export default RollbackInvoiceModal;
